import { useInfiniteQuery } from "@tanstack/react-query";

import { authHeader } from "@/utils/headers";
import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { GetPlaygroundSessionChatMessagesResponseI } from "@/queries/playground/useAPIGetPlaygroundSessionChatMessages";

export interface UseInfiniteGetPlaygroundSessionChatMessagesParamsI {
  workspaceId: number;
  sessionId: string;
  variableSetIndex: number;
  perPage?: number;
  enabled?: boolean;
}

export const useInfiniteGetPlaygroundSessionChatMessages = ({
  workspaceId,
  sessionId,
  variableSetIndex,
  perPage = 50,
  enabled = true,
}: UseInfiniteGetPlaygroundSessionChatMessagesParamsI) => {
  const userToken = useAuth()?.userToken;

  return useInfiniteQuery<GetPlaygroundSessionChatMessagesResponseI>({
    queryKey: [
      "playground-session-chat-messages",
      "infinite",
      workspaceId,
      sessionId,
      variableSetIndex,
      perPage,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const baseUrl = ENDPOINTS.playground_session_chat_messages(workspaceId, sessionId);
      const url = new URL(baseUrl);
      url.searchParams.set("variable_set_index", String(variableSetIndex));
      url.searchParams.set("page", String(pageParam));
      url.searchParams.set("per_page", String(perPage));

      const response = await fetch(url.toString(), {
        headers: authHeader(userToken!),
      });
      return handleResponse(response);
    },
    getNextPageParam: lastPage => (lastPage.has_next ? lastPage.page + 1 : undefined),
    enabled: enabled && !!userToken && !!sessionId,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};
